import React from 'react';
import { ChevronRight } from 'lucide-react';
import { formatHistoryAction, getSekaLiveActionIndicator } from '../game/sekaHistoryUi';
import { cn } from './utils/cn';

/** Компактная полоска цветных точек — последние ходы (для свёрнутой кнопки «Ходы раздачи»). */
export function LiveSekaHandMovesTrail({ actions, max = 8, className }) {
  const list = Array.isArray(actions) ? actions : [];
  const tail = list.slice(-max);
  if (tail.length === 0) return null;
  return (
    <span
      className={cn('inline-flex max-w-[min(100%,9rem)] items-center justify-end gap-0.5 overflow-hidden', className)}
      aria-hidden
    >
      {tail.map((a, i) => {
        const ind = getSekaLiveActionIndicator(a);
        return (
          <span
            key={`trail-${i}-${a?.playerId ?? 'x'}-${String(a?.action ?? '')}`}
            className={cn('h-1.5 w-1.5 shrink-0 rounded-full ring-1 ring-black/40', ind.dot)}
            title={`${a?.playerName ?? '—'}: ${formatHistoryAction(a)}`}
          />
        );
      })}
    </span>
  );
}

function liveActionKey(a, actionIdx) {
  return `live-act-${actionIdx}-${a?.playerId ?? 'x'}-${String(a?.action ?? '')}-${a?.timestamp ? String(a.timestamp) : actionIdx}`;
}

/** Краткая сумма для подписи (фишки), без дублирования основного текста. */
function actionAmountHint(a) {
  const action = String(a?.action || '').toUpperCase();
  const paid = Number(a?.paid);
  if (action === 'CALL' && Number.isFinite(paid) && paid > 0) {
    return `−${paid.toLocaleString('ru-RU')}`;
  }
  if (action === 'RAISE') {
    const paidR = Number(a?.paid);
    if (Number.isFinite(paidR) && paidR > 0) return `−${paidR.toLocaleString('ru-RU')}`;
  }
  return null;
}

/** Живая лента ходов раздачи: цветные метки типа действия, олл-ин, цепочка + список. */
export function LiveSekaHandMovesPanel({ actions, emptyHint }) {
  const list = Array.isArray(actions) ? actions : [];

  if (list.length === 0) {
    return (
      <>
        <p className="text-center text-[9px] font-semibold uppercase tracking-wide text-amber-200/85">
          Только действия · карты соперников не показываются
        </p>
        <p className="mt-2 text-center text-[10px] leading-snug text-white/55">
          {emptyHint ??
            'Пока нет записанных ходов — лента обновится после первого действия в раздаче.'}
        </p>
      </>
    );
  }

  return (
    <>
      <p className="text-center text-[9px] font-semibold uppercase tracking-wide text-amber-200/85">
        Только действия · карты соперников не показываются
      </p>
      <p className="mt-1 text-center text-[8px] text-white/45">
        Метки: Ч чек · См смотр · П пас · К колл · Р рейз · В вскрытие · А авто
      </p>

      <div className="mt-1.5 flex flex-wrap items-center gap-x-0.5 gap-y-1 text-[10px] leading-snug">
        {list.map((a, i) => {
          const ind = getSekaLiveActionIndicator(a);
          const line = `${i + 1}. ${a?.playerName ?? '—'}: ${formatHistoryAction(a)}`;
          return (
            <React.Fragment key={`live-chain-${liveActionKey(a, i)}`}>
              {i > 0 ? (
                <ChevronRight className="mx-0.5 h-3.5 w-3.5 shrink-0 text-amber-500/70" aria-hidden />
              ) : null}
              <span
                className={cn(
                  'inline-flex max-w-full items-center gap-1 rounded-md border border-white/12 bg-black/40 px-1.5 py-0.5 text-amber-100/95 shadow-sm'
                )}
              >
                <span className={cn('h-2 w-2 shrink-0 rounded-full ring-1 ring-black/30', ind.dot)} aria-hidden />
                <span
                  className={cn(
                    'shrink-0 rounded px-1 py-px text-[8px] font-extrabold leading-none tracking-tight',
                    ind.badge
                  )}
                  title={String(a?.action || '')}
                >
                  {ind.abbr}
                </span>
                <span className="min-w-0 truncate">{line}</span>
                {a?.allIn ? (
                  <span
                    className="shrink-0 rounded border border-orange-300/40 bg-orange-500/20 px-1 py-px text-[7px] font-bold uppercase leading-none text-orange-100"
                    title="Олл-ин"
                  >
                    АИ
                  </span>
                ) : null}
              </span>
            </React.Fragment>
          );
        })}
      </div>

      <ol className="mt-2 space-y-1.5 border-t border-white/10 pt-2">
        {list.map((a, actionIdx) => {
          const ind = getSekaLiveActionIndicator(a);
          const hint = actionAmountHint(a);
          return (
            <li
              key={liveActionKey(a, actionIdx)}
              className="flex gap-2 rounded-lg border border-white/8 bg-black/30 py-1.5 pl-1 pr-2 shadow-sm"
            >
              <div className={cn('w-1 shrink-0 self-stretch rounded-full', ind.accentBar)} aria-hidden />
              <div className="flex min-w-0 flex-1 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2">
                <div className="flex min-w-0 flex-1 items-center gap-1.5">
                  <span className="w-5 shrink-0 text-right font-mono text-[9px] tabular-nums text-white/40">
                    {actionIdx + 1}
                  </span>
                  <span
                    className={cn(
                      'inline-flex h-5 min-w-[1.25rem] shrink-0 items-center justify-center rounded border px-1 text-[9px] font-extrabold leading-none',
                      ind.badge
                    )}
                    title={String(a?.action || '')}
                  >
                    {ind.abbr}
                  </span>
                  <span className="truncate text-[10px] font-medium text-white/90">{a?.playerName ?? '—'}</span>
                  {a?.allIn ? (
                    <span className="shrink-0 rounded border border-orange-300/45 bg-orange-500/20 px-1 py-px text-[7px] font-bold uppercase text-orange-100">
                      олл-ин
                    </span>
                  ) : null}
                </div>
                <div className="flex shrink-0 flex-col items-end gap-0 pl-6 sm:pl-0">
                  <span className="text-[10px] text-amber-100/95">{formatHistoryAction(a)}</span>
                  {hint ? (
                    <span className="font-mono text-[8px] tabular-nums text-white/45">{hint}</span>
                  ) : null}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </>
  );
}
